/**
 * PitchPrint Centralized State Manager
 * Single source of truth for all PitchPrint state across the site
 * Version: 1.0.0
 */
window.PitchPrintStateManager = (() => {
  'use strict';

  // ⚠️ CRITICAL: Do NOT run on main cart page to avoid conflicts
  if (window.location.pathname === '/cart') {
    console.log('[StateManager] 🚫 Skipping on main cart page to avoid PitchPrint conflicts');
    // Return minimal API to prevent errors in dependent scripts
    return {
      on: () => {},
      emit: () => {},
      getState: () => ({}),
      getProjectId: () => null,
      getVariantData: () => null,
      isVariantLocked: () => false,
      isSystemReady: () => false,
      isToastActive: () => false,
      setProject: () => {},
      clearProject: () => {},
      lockVariants: () => {},
      unlockVariants: () => {},
      setSystemReady: () => {},
      setToastActive: () => {},
      setToastInactive: () => {},
      setCartPreview: () => {},
      getCartPreview: () => null,
      addError: () => {},
      clearErrors: () => {}
    };
  }

  // Debug mode toggle - can be enabled via console
  const DEBUG = window.PITCHPRINT_DEBUG || false;
  
  // Central state object
  let state = {
    projectId: null,
    variantData: null,
    variantLocked: false,
    systemReady: false,
    cartPreviews: new Map(),
    toastActive: false,
    errors: [],
    // File upload state
    uploadMode: null, // 'upload_now' | 'send_later' | null
    uploadData: null, // { uploadId, files: { front, back }, previews: { front, back }, shape, sides }
    uploadActive: false,
    // Design method state (for toggle)
    designMethod: null // 'file_upload' | 'pitchprint' | null
  };

  // Event system for cross-component communication
  const events = new EventTarget();
  
  // Debug logging helper
  const log = (...args) => DEBUG && console.log('[StateManager]', ...args);

  // Public API
  const StateManager = {
    // =====================================
    // STATE GETTERS
    // =====================================

    getState: () => ({ ...state }),
    getProjectId: () => state.projectId,
    getVariantData: () => state.variantData,
    isVariantLocked: () => state.variantLocked,
    isSystemReady: () => state.systemReady,
    isToastActive: () => state.toastActive,
    getUploadMode: () => state.uploadMode,
    getUploadData: () => state.uploadData,
    isUploadActive: () => state.uploadActive,
    getDesignMethod: () => state.designMethod,

    // =====================================
    // PROJECT MANAGEMENT
    // =====================================
    
    setProject(projectData) {
      const oldProjectId = state.projectId;
      state.projectId = projectData.projectId;
      state.variantData = projectData.variant;

      // Store project metadata for toast preview and other systems
      if (projectData.numPages !== undefined) {
        state.numPages = projectData.numPages;
      }
      if (projectData.shape) {
        state.shape = projectData.shape;
      }

      log('Project set:', projectData);

      if (oldProjectId !== projectData.projectId) {
        this.emit('project:changed', projectData);
      }

      if (projectData.projectId) {
        this.lockVariants();
        this.emit('project:restored', projectData);
      }
    },

    clearProject() {
      const hadProject = !!state.projectId;
      state.projectId = null;
      state.variantData = null;
      state.numPages = null;
      state.shape = null;

      log('Project cleared');

      if (hadProject) {
        this.unlockVariants();
        this.emit('project:cleared');
      }
    },

    // =====================================
    // VARIANT CONTROL
    // =====================================
    
    lockVariants() {
      if (!state.variantLocked) {
        state.variantLocked = true;
        log('Variants locked');
        this.emit('variants:locked');
      }
    },

    unlockVariants() {
      if (state.variantLocked) {
        state.variantLocked = false;
        log('Variants unlocked');
        this.emit('variants:unlocked');
      }
    },

    // =====================================
    // SYSTEM STATUS
    // =====================================
    
    setSystemReady() {
      if (!state.systemReady) {
        state.systemReady = true;
        log('System ready');
        this.emit('system:ready');
      }
    },

    // =====================================
    // CART PREVIEW MANAGEMENT
    // =====================================
    
    setCartPreview(lineId, previewData) {
      state.cartPreviews.set(lineId, previewData);
      log('Cart preview set for line:', lineId);
      this.emit('cart:preview:updated', { lineId, previewData });
    },

    getCartPreview(lineId) {
      return state.cartPreviews.get(lineId);
    },

    clearCartPreviews() {
      state.cartPreviews.clear();
      log('All cart previews cleared');
      this.emit('cart:previews:cleared');
    },

    // =====================================
    // TOAST WIDGET MANAGEMENT
    // =====================================
    
    activateToast(projectData) {
      if (!state.toastActive) {
        state.toastActive = true;
        log('Toast activated');
        this.emit('toast:activate', projectData);
      }
    },

    deactivateToast() {
      if (state.toastActive) {
        state.toastActive = false;
        log('Toast deactivated');
        this.emit('toast:deactivate');
      }
    },

    // =====================================
    // UPLOAD MANAGEMENT
    // =====================================

    setUploadMode(mode) {
      state.uploadMode = mode;
      log('Upload mode set:', mode);
      this.emit('upload:mode:changed', mode);
    },

    setUploadData(uploadData) {
      state.uploadData = uploadData;
      state.uploadActive = !!uploadData;
      log('Upload data set:', uploadData);
      this.emit('upload:data:updated', uploadData);

      if (uploadData) {
        this.lockVariants();
        this.emit('upload:completed', uploadData);
      }
    },

    clearUploadData() {
      const hadUpload = !!state.uploadData;
      state.uploadData = null;
      state.uploadMode = null;
      state.uploadActive = false;

      log('Upload data cleared');

      if (hadUpload) {
        this.unlockVariants();
        this.emit('upload:cleared');
      }
    },

    // =====================================
    // DESIGN METHOD MANAGEMENT
    // =====================================

    setDesignMethod(method) {
      const oldMethod = state.designMethod;

      if (oldMethod === method) {
        log('Design method already set to:', method);
        return;
      }

      state.designMethod = method;
      log('Design method changed:', oldMethod, '→', method);
      this.emit('design:method:changed', method);

      // Auto-set based on what data is active
      if (method === 'pitchprint' && state.projectId) {
        log('PitchPrint method selected with active project');
      } else if (method === 'file_upload' && state.uploadData) {
        log('File upload method selected with active upload');
      }
    },

    clearDesignMethod() {
      state.designMethod = null;
      log('Design method cleared');
    },

    // =====================================
    // ERROR HANDLING
    // =====================================
    
    logError(error, context) {
      const errorEntry = { 
        error: error.toString(), 
        context, 
        timestamp: Date.now(),
        stack: error.stack || 'No stack trace'
      };
      
      state.errors.push(errorEntry);
      
      // Keep only last 10 errors to prevent memory bloat
      if (state.errors.length > 10) {
        state.errors.shift();
      }
      
      console.log('[PitchPrint Error]', context, error);
      this.emit('error:logged', errorEntry);
    },

    // =====================================
    // EVENT SYSTEM
    // =====================================
    
    on(event, callback) {
      events.addEventListener(event, callback);
      log('Event listener added:', event);
    },

    off(event, callback) {
      events.removeEventListener(event, callback);
      log('Event listener removed:', event);
    },

    emit(event, data) {
      log('Emitting event:', event, data);
      events.dispatchEvent(new CustomEvent(event, { detail: data }));
    },

    // =====================================
    // DEBUG TOOLS
    // =====================================
    
    debug: {
      enableDebug() {
        window.PITCHPRINT_DEBUG = true;
        console.log('🐛 PitchPrint Debug Mode: ENABLED');
        console.log('Available commands:');
        console.log('- PitchPrintStateManager.debug.validateState()');
        console.log('- PitchPrintStateManager.debug.simulateUserScenario()');
        console.log('- PitchPrintStateManager.debug.getErrors()');
        console.log('- togglePitchPrintDebug()');
      },

      disableDebug() {
        window.PITCHPRINT_DEBUG = false;
        console.log('🐛 PitchPrint Debug Mode: DISABLED');
      },

      getErrors() {
        return state.errors;
      },

      getState() {
        return state;
      },

      reset() {
        const oldState = { ...state };
        state = {
          projectId: null,
          variantData: null,
          variantLocked: false,
          systemReady: false,
          cartPreviews: new Map(),
          toastActive: false,
          errors: []
        };
        console.log('🔄 State reset:', { oldState, newState: state });
        StateManager.emit('debug:state:reset');
      },

      // Test the critical user scenario
      simulateUserScenario() {
        console.log('🧪 Testing user scenario: save → navigate away → return');
        
        // Step 1: Simulate project save
        const testProjectData = {
          projectId: 'test123456789abcdef',
          variant: { 
            id: 'variant123', 
            options: ['Red', 'Large'] 
          }
        };
        
        console.log('1️⃣ Simulating project save...');
        StateManager.setProject(testProjectData);
        
        // Step 2: Simulate navigation away (state should persist)
        setTimeout(() => {
          console.log('2️⃣ Simulating page navigation away (state persists)...');
          console.log('   Current project ID:', StateManager.getProjectId());
          console.log('   Variants locked:', StateManager.isVariantLocked());
        }, 1000);
        
        // Step 3: Simulate return to page
        setTimeout(() => {
          console.log('3️⃣ Simulating return to page...');
          console.log('   Should trigger: project restoration, variant locking, toast activation');
          StateManager.emit('project:restored', testProjectData);
          console.log('✅ User scenario test complete');
        }, 2000);
      },

      // Validate current state and log summary
      validateState() {
        const currentState = StateManager.getState();
        console.table({
          'Project ID': currentState.projectId || 'None',
          'Variant Locked': currentState.variantLocked,
          'System Ready': currentState.systemReady,
          'Toast Active': currentState.toastActive,
          'Cart Previews': currentState.cartPreviews.size,
          'Total Errors': currentState.errors.length
        });
        
        if (currentState.errors.length > 0) {
          console.log('Recent errors:');
          currentState.errors.slice(-3).forEach((error, index) => {
            console.log(`${index + 1}. [${error.context}] ${error.error}`);
          });
        }
        
        return currentState;
      },

      // Performance monitoring
      getPerformanceMetrics() {
        return {
          memoryUsage: performance.memory ? {
            used: Math.round(performance.memory.usedJSHeapSize / 1024 / 1024) + ' MB',
            total: Math.round(performance.memory.totalJSHeapSize / 1024 / 1024) + ' MB',
            limit: Math.round(performance.memory.jsHeapSizeLimit / 1024 / 1024) + ' MB'
          } : 'Not available',
          timing: performance.timing ? {
            pageLoadTime: (performance.timing.loadEventEnd - performance.timing.navigationStart) + ' ms',
            domContentLoaded: (performance.timing.domContentLoadedEventEnd - performance.timing.navigationStart) + ' ms'
          } : 'Not available',
          stateSize: JSON.stringify(state).length + ' bytes'
        };
      }
    }
  };

  // Initialize debug mode if enabled
  if (DEBUG) {
    StateManager.debug.enableDebug();
  }

  log('PitchPrint StateManager initialized');
  
  return StateManager;
})();

// =====================================
// GLOBAL DEBUG HELPER
// =====================================

// Easy debug toggle function accessible from console
window.togglePitchPrintDebug = () => {
  if (window.PITCHPRINT_DEBUG) {
    window.PitchPrintStateManager.debug.disableDebug();
  } else {
    window.PitchPrintStateManager.debug.enableDebug();
  }
};